/** Задания дня — представление без стора, всё приходит пропсами. */
import { BoostIcon, Button, Icon, type IconName, WindowShell, compactCoins } from "../kit";
import { type DailyTaskClaim, type DailyTaskId, type DailyTasksState } from "@newfungame/contracts";
import { getLocale } from "../../../i18n";
import { splitDuration } from "../welcome-back/model";
import { useT } from "../../../app";
import "./styles.css";

const TASK_ICON: Record<DailyTaskId, IconName> = { kills: "target", waves: "shield", upgrades: "wrench" };

export interface TasksViewProps {
  tasks: DailyTasksState;
  /** Что можно забрать сейчас. */
  claimable: readonly DailyTaskClaim[];
  secondsLeft: number;
  onClaim(task: DailyTaskClaim): void;
  onClose(): void;
}

function TaskReward(props: {
  id: DailyTaskClaim;
  coins: number;
  claimed: boolean;
  claimable: boolean;
  onClaim(task: DailyTaskClaim): void;
}) {
  const t = useT();
  const coins = compactCoins(props.coins, getLocale());
  // ширина колонки — у обёртки: на самой иконке min-width растягивал кадр атласа
  if (props.claimed)
    return (
      <span class="win-task__done">
        <Icon name="check" size={30} />
      </span>
    );
  return (
    <div class="win-task__reward">
      <span class="win-task__coins">
        <Icon name="coin" size={20} />+{coins}
      </span>
      {props.claimable && (
        <Button tone="green" onClick={() => props.onClaim(props.id)} data-testid={`task-claim-${props.id}`}>
          {t("win.tasks.claim")}
        </Button>
      )}
    </div>
  );
}

/** Окно заданий (макет 09, монеты вместо кристаллов и сундуков). */
export function TasksView({ tasks, claimable, secondsLeft, onClaim, onClose }: TasksViewProps) {
  const t = useT();
  const { h, m } = splitDuration(secondsLeft);
  const done = tasks.tasks.filter((x) => x.progress >= x.target).length;
  return (
    <WindowShell kind="tasks" title={t("win.tasks.title")}>
      <p class="win-tasks__reset" data-testid="tasks-reset">
        <Icon name="clock" size={18} />
        {t("win.tasks.resets", { h, m })}
      </p>
      <ul class="win-tasks">
        {tasks.tasks.map((x) => {
          const pct = Math.round((x.progress / x.target) * 100);
          return (
            <li
              key={x.id}
              class={`win-task${claimable.includes(x.id) ? " win-task--ready" : ""}`}
              data-testid={`task-${x.id}`}
            >
              <span class="win-task__icon">
                <Icon name={TASK_ICON[x.id]} size={30} />
              </span>
              <div class="win-task__body">
                <span class="win-task__title">{t(`win.tasks.${x.id}`, { n: x.target })}</span>
                <span class="win-bar win-task__bar" role="progressbar" aria-valuenow={pct}>
                  <span class="win-bar__fill" style={{ width: `${pct}%` }} />
                  <span class="win-task__count">
                    {x.progress}/{x.target}
                  </span>
                </span>
              </div>
              <TaskReward
                id={x.id}
                coins={x.coins}
                claimed={x.claimed}
                claimable={claimable.includes(x.id)}
                onClaim={onClaim}
              />
            </li>
          );
        })}
        <li
          class={`win-task win-task--bonus${claimable.includes("bonus") ? " win-task--ready" : ""}`}
          data-testid="task-bonus"
        >
          <span class="win-task__icon">
            <BoostIcon boostId="boost_coin_rush" size={34} />
          </span>
          <div class="win-task__body">
            <span class="win-task__title">{t("win.tasks.bonus")}</span>
            <span class="win-bar win-task__bar" role="progressbar" aria-valuenow={Math.round((done / 3) * 100)}>
              <span class="win-bar__fill" style={{ width: `${Math.round((done / 3) * 100)}%` }} />
              <span class="win-task__count">{done}/3</span>
            </span>
          </div>
          <TaskReward
            id="bonus"
            coins={tasks.bonus.coins}
            claimed={tasks.bonus.claimed}
            claimable={claimable.includes("bonus")}
            onClaim={onClaim}
          />
        </li>
      </ul>
      <div class="win-actions">
        <Button tone="blue" onClick={onClose} data-testid="tasks-close">
          {t("win.tasks.close")}
        </Button>
      </div>
    </WindowShell>
  );
}
